import {
  Calendar,
  ChevronRight,
  FileMinus,
  LibraryBig,
  SquareArrowOutUpRight,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell } from "recharts";
import { getProjectSites } from "../../../Services/projectServices";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import { useProjects } from "../../Project/Hooks/useProjects";
import { TOAST_MESSAGES } from "../Constants";
import { SITE_DETAILS, VIEW_PROJECT } from "../../../routes";
import { useDashboard } from "../Hooks/useDashboard";
import {
  getStatusIconAndColor,
  TASK_STATUS_LABELS,
} from "../../Sites/Constants";

const COLORS = ["#57cc99", "#e5e7eb"];

const DonutChart = ({ value }) => {
  const data = [
    { name: "Completed", value: value },
    { name: "Remaining", value: 100 - value },
  ];

  return (
    <div className="relative w-50 h-50 flex items-center justify-center">
      <PieChart width={160} height={160}>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={40}
          outerRadius={70}
          startAngle={90}
          endAngle={-270}
          paddingAngle={value === 100 ? 0 : 2}
          dataKey="value"
          cornerRadius={2}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index]} />
          ))}
        </Pie>
      </PieChart>
      <div className="absolute text-xl font-bold text-gray-700">{value}%</div>
    </div>
  );
};

const ProjectExplorer = ({ filter }) => {
  const [projectSites, setProjectSites] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [loadingSites, setLoadingSites] = useState(false);
  const [taskSummary, setTaskSummary] = useState({});
  const [loadingTasks, setLoadingTasks] = useState(false);
  const { fetchTaskKPI } = useDashboard();

  const {
    projects,
    fetchProjects,
    setFilters,
    filters,
    loading: loadingProjects,
  } = useProjects();

  const navigate = useNavigate();

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setSelectedSite(null);
  };

  const handleSiteClick = async (siteId) => {
    setSelectedSite(siteId);
    setTaskSummary({});
    setLoadingSites(true);

    try {
      const res = await fetchTaskKPI({ site: siteId });
      if (res) {
        setTaskSummary(res);
      } else {
        toast.error(TOAST_MESSAGES.FETCH_COMPLETION_FAILED);
      }
    } catch (err) {
      toast.error(TOAST_MESSAGES.FETCH_COMPLETION_FAILED);
    } finally {
      setLoadingSites(false);
    }
  };

  const fetchSites = async () => {
    setLoadingSites(true);
    try {
      const res = await getProjectSites(selectedProject, {
        limit: 0,
        sites: "all",
      });
      if (res.status === 200) {
        setProjectSites(res.data);
      } else {
        toast.error(TOAST_MESSAGES.FETCH_COMPLETION_FAILED);
      }
    } catch {
      toast.error(TOAST_MESSAGES.FETCH_COMPLETION_FAILED);
    } finally {
      setLoadingSites(false);
    }
  };

  useEffect(() => {
    if (selectedProject) {
      fetchSites();
    }
  }, [selectedProject]);

  useEffect(() => {
    setFilters({ project: filter, status: "IN_PROGRESS" });
    setSelectedProject(null);
    setSelectedSite(null);
  }, [filter]);

  useEffect(() => {
    setSelectedProject(projects[0]?.id);
  }, [projects]);

  useEffect(() => {
    fetchProjects();
  }, [filters]);

  const renderDetails = () => {
    if (loadingSites) {
      return (
        <div className="flex flex-col gap-4 min-h-[360px]">
          <div className=" bg-gray-200 animate-pulse rounded-lg" />
          <div className=" bg-gray-200 animate-pulse rounded-lg" />
        </div>
      );
    }

    if (selectedSite && selectedProject) {
      const site = projectSites.find((s) => s.id === selectedSite);
      const projectObj = projects.find((p) => p.id === selectedProject);
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white space-y-4 max-h-[50vh] overflow-auto"
        >
          <div className="flex justify-between items-start">
            <h3 className="font-bold text-gray-800">Site Details</h3>
            <button
              onClick={() =>
                navigate(SITE_DETAILS.replace(":instanceId", site.id))
              }
              className="text-blue-600 flex items-center gap-1 hover:underline"
              title="View Details"
            >
              <SquareArrowOutUpRight size={16} />
            </button>
          </div>
          <div className="flex justify-center">
            <DonutChart value={site?.completion_percentage} />
          </div>
          <div className="space-y-1">
            <p>
              <span className="font-semibold">Project:</span> {projectObj.name}
            </p>
            <p>
              <span className="font-semibold">Site:</span> {site.site_name}
            </p>
          </div>
          <div className="space-y-1 mt-2">
            {loadingTasks ? (
              <p>Loading tasks...</p>
            ) : Object.keys(taskSummary).length > 0 ? (
              <div>
                <p className="font-semibold">Task Summary:</p>
                <ul className="ml-4 flex justify-between gap-2">
                  {Object.entries(taskSummary).map(([status, count]) => {
                    const { color } = getStatusIconAndColor(
                      status.toUpperCase()
                    );
                    return (
                      <li
                        key={status}
                        className={`flex flex-col items-center ${color} px-2 py-1 rounded min-w-[30%]`}
                      >
                        <span className="font-medium capitalize">
                          {TASK_STATUS_LABELS[status.toUpperCase()] || status}
                        </span>
                        <span>{count} </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : (
              <p>No tasks for this site</p>
            )}
          </div>
        </motion.div>
      );
    }

    const projectObj = projects.find((p) => p.id === selectedProject);
    if (projectObj) {
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white space-y-4"
        >
          <div className="flex justify-between items-start">
            <h3 className="font-bold  text-gray-800">Project Details</h3>
            <button
              onClick={() =>
                navigate(VIEW_PROJECT.replace(":instanceId", projectObj?.id))
              }
              className="text-blue-600 flex items-center gap-1 hover:underline"
              title="View Details"
            >
              <SquareArrowOutUpRight size={16} />
            </button>
          </div>
          <div className="flex justify-center">
            <DonutChart value={projectObj?.completion_percentage} />
          </div>
          <div className="flex justify-between items-start">
            <p>
              <span className="font-semibold">Project:</span> {projectObj?.name}
            </p>
          </div>
        </motion.div>
      );
    }

    return (
      <div className="flex flex-col justify-center items-center h-full w-full text-gray-400">
        <div className="bg-gray-100 p-2 rounded-full">
          <LibraryBig className="text-gray-300" />
        </div>
        <p className="text-gray-400">
          Select a project or site to view details
        </p>
      </div>
    );
  };

  const renderProjectList = () => {
    if (loadingProjects) {
      return new Array(3)
        .fill(0)
        .map((_, idx) => (
          <div
            key={idx}
            className="h-5 bg-gray-200 animate-pulse rounded mb-2"
          />
        ));
    }

    return projects?.map((project) => (
      <li
        key={project.id}
        className={`flex items-center justify-between gap-2 mb-1 cursor-pointer p-2 font-medium rounded transition-colors ${
          selectedProject === project.id
            ? "bg-blue-100 text-blue-700"
            : "hover:bg-gray-100"
        }`}
        onClick={() => handleProjectClick(project.id)}
      >
        <FileMinus size={16} />
        <span className="flex-1 truncate">{project.name}</span>
        <ChevronRight size={14} className="text-gray-400" />
      </li>
    ));
  };

  const renderSiteList = () => {
    if (loadingSites) {
      return new Array(3)
        .fill(0)
        .map((_, idx) => (
          <div
            key={idx}
            className="h-10 bg-gray-200 animate-pulse rounded mb-2"
          />
        ));
    }

    if (!projectSites || projectSites.length === 0) {
      return (
        <div className="flex flex-col justify-center items-center h-full text-gray-400">
          <LibraryBig className="text-gray-300 mb-2" />
          <p>No Sites for project...</p>
        </div>
      );
    }

    return projectSites.map((site) => (
      <li
        key={site.id}
        className={`flex items-center justify-between gap-2 mb-1 cursor-pointer p-2 font-medium rounded transition-colors ${
          selectedSite === site.id
            ? "bg-blue-100 text-blue-700"
            : "hover:bg-gray-100"
        }`}
        onClick={() => handleSiteClick(site.id)}
      >
        <Calendar size={16} />
        <span className="flex-1 truncate">{site.site_name}</span>
        <ChevronRight size={14} className="text-gray-400" />
      </li>
    ));
  };

  return (
    <div className="">
      <h2 className="text-xl font-semibold mb-4">Projects Overview</h2>
      <div className="flex max-h-[53vh] overflow-y-auto">
        {/* Projects */}
        <motion.div
          animate={{ width: selectedSite ? "24%" : "30%" }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="border-r border-gray-200 flex flex-col p-2  min-h-[45vh]"
        >
          <h2 className="font-bold p-2">Projects</h2>
          <div className="flex-1 overflow-y-auto max-h-[calc(100vh-150px)] custom-scroll">
            <ul>{renderProjectList()}</ul>
          </div>
        </motion.div>

        {/* Sites */}
        <motion.div
          animate={{ width: selectedSite ? "26%" : "30%" }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="border-r border-gray-200 flex flex-col p-2"
        >
          <h2 className="font-bold p-2">Sites</h2>
          <div className="flex-1 overflow-y-auto max-h-[calc(100vh-150px)]">
            <ul>{renderSiteList()}</ul>
          </div>
        </motion.div>

        {/* Details */}
        <motion.div
          animate={{ width: selectedSite ? "55%" : "40%" }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="p-2"
        >
          {renderDetails()}
        </motion.div>
      </div>
    </div>
  );
};

export default ProjectExplorer;
